import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { 
  Bell, 
  X, 
  Trash2, 
  MessageSquare, 
  AlertCircle, 
  Info, 
  Gift,
  CheckCheck,
  Image as ImageIcon,
  Home,
  Sparkles
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
import Footer from '@/components/layout/Footer';
import { useNotifications } from '@/hooks/useNotifications';
import axios from '@/utils/api';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId: urlClientId } = useParams();
  const [searchParams] = useSearchParams();
  const { theme: globalTheme, isGradientMode } = useTheme();
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  
  // B2B client branding state
  const [b2bClient, setB2bClient] = useState(null);
  const [b2bTheme, setB2bTheme] = useState(null);
  const [b2bLoading, setB2bLoading] = useState(true);
  
  // Determine if we're on a B2B portal URL
  const isOnB2BPortalUrl = location.pathname.includes('/portal/');
  const dashboardUrl = isOnB2BPortalUrl && urlClientId ? `/portal/${urlClientId}/dashboard` : '/dashboard';
  
  // Use B2B theme if available, otherwise global theme
  const theme = b2bTheme || globalTheme;
  
  // Fetch B2B client branding when on B2B portal URL
  useEffect(() => {
    const fetchB2BClient = async () => {
      if (!isOnB2BPortalUrl || !urlClientId) {
        setB2bLoading(false);
        return;
      }
      
      setB2bLoading(true);
      try {
        // Use the correct endpoint that gets client config for the current user
        const res = await axios.get(`/api/user/client-config`, {
          withCredentials: true
        });
        if (res.data.has_client && res.data.client) {
          setB2bClient(res.data.client);
          // Use the pre-merged theme from the backend if available
          if (res.data.theme) {
            setB2bTheme({
              ...res.data.theme,
              // Also map to the alternate field names
              background: res.data.theme.background_primary,
              background_secondary: res.data.theme.background_secondary || res.data.theme.card_color,
              primary: res.data.theme.primary_color,
              secondary: res.data.theme.secondary_color,
              accent: res.data.theme.accent_color,
              text: res.data.theme.text_primary,
              text_secondary: res.data.theme.text_secondary,
              border: res.data.theme.border_color,
            });
          } else if (res.data.client.theme) {
            // Fallback to client.theme if theme not provided separately
            const clientTheme = res.data.client.theme;
            setB2bTheme({
              ...clientTheme,
              background: clientTheme.background_primary || clientTheme.background_color,
              background_secondary: clientTheme.background_secondary || clientTheme.card_color,
              primary: clientTheme.primary_color || clientTheme.brand_color,
              secondary: clientTheme.secondary_color,
              accent: clientTheme.accent_color,
              text: clientTheme.text_primary || clientTheme.text_color,
              text_secondary: clientTheme.text_secondary,
              border: clientTheme.border_color,
            });
          }
        }
      } catch (err) {
        console.error('Error fetching B2B client config:', err);
      } finally {
        setB2bLoading(false);
      }
    };
    
    fetchB2BClient();
  }, [isOnB2BPortalUrl, urlClientId]);
  
  // Apply B2B theme to CSS variables when theme is loaded
  useEffect(() => {
    if (isOnB2BPortalUrl && b2bTheme) {
      const root = document.documentElement;
      if (b2bTheme.accent) root.style.setProperty('--theme-accent', b2bTheme.accent);
      if (b2bTheme.primary) root.style.setProperty('--theme-primary', b2bTheme.primary);
      if (b2bTheme.text) root.style.setProperty('--theme-text', b2bTheme.text);
      if (b2bTheme.text_secondary) root.style.setProperty('--theme-text-secondary', b2bTheme.text_secondary);
    }
  }, [isOnB2BPortalUrl, b2bTheme]);
  
  // Close modal handler - handles any edge cases
  const closeModal = () => {
    if (isClosing) return; // Prevent double-close
    setIsClosing(true);
    setSelectedNotification(null);
    // Reset closing state after animation
    setTimeout(() => setIsClosing(false), 100);
  };
  
  // Use shared hook for consistent auth handling
  const {
    notifications,
    loading,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getNotificationById
  } = useNotifications();

  const [hasOpenedFromUrl, setHasOpenedFromUrl] = useState(false);

  // Auto-open notification from URL param (only once)
  useEffect(() => {
    if (hasOpenedFromUrl) return; // Only open from URL once
    
    const notifId = searchParams.get('id');
    if (notifId && notifications.length > 0) {
      const targetNotif = getNotificationById(notifId);
      if (targetNotif) {
        setSelectedNotification(targetNotif);
        setHasOpenedFromUrl(true);
        // Mark as read when opened
        if (!targetNotif.is_read) {
          markAsRead(notifId);
        }
      }
    }
  }, [searchParams, notifications, getNotificationById, markAsRead, hasOpenedFromUrl]);

  const handleMarkAsRead = async (notificationId) => {
    const success = await markAsRead(notificationId);
    if (!success) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    const success = await markAllAsRead();
    if (success) {
      toast.success('All notifications marked as read');
    } else {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (notificationId) => {
    const success = await deleteNotification(notificationId);
    if (success) {
      toast.success('Notification deleted');
      closeModal();
    } else {
      toast.error('Failed to delete notification');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'alert': return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'promotion': return <Gift className="w-5 h-5 text-green-400" />;
      case 'announcement': return <MessageSquare className="w-5 h-5" style={{ color: 'var(--theme-accent)' }} />;
      default: return <Info className="w-5 h-5" style={{ color: 'var(--theme-accent)' }} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'alert': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'promotion': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'announcement': return 'bg-[var(--theme-accent)]/20 text-[var(--theme-accent)] border-[var(--theme-accent)]/30';
      default: return 'bg-[var(--theme-accent)]/20 text-[var(--theme-accent)] border-[var(--theme-accent)]/30';
    }
  };

  // Get B2B theme colors for inline styles when CSS variables won't work
  const b2bColors = b2bTheme ? {
    background: b2bTheme.background || b2bTheme.background_primary || '#0F172A',
    backgroundSecondary: b2bTheme.background_secondary || '#1E293B',
    primary: b2bTheme.primary || b2bTheme.primary_color || '#06B6D4',
    secondary: b2bTheme.secondary || b2bTheme.secondary_color || '#14B8A6',
    accent: b2bTheme.accent || b2bTheme.accent_color || '#06B6D4',
    text: b2bTheme.text || b2bTheme.text_primary || '#F8FAFC',
    textSecondary: b2bTheme.text_secondary || '#94A3B8',
    border: b2bTheme.border || b2bTheme.border_color || '#334155',
  } : null;

  return (
    <div 
      className="diary-app min-h-screen flex flex-col p-4 lg:p-8"
      style={{ 
        background: isOnB2BPortalUrl && b2bColors 
          ? b2bColors.background 
          : 'var(--theme-background)' 
      }}
      data-testid="notifications-page"
    >
      {/* Header - Shows B2B branding or main app branding */}
      <header className="max-w-7xl mx-auto mb-4 lg:mb-6 pt-2 sm:pt-4 w-full">
        <div className="flex flex-col items-center gap-2 lg:gap-4">
          {/* Show loading placeholder while fetching B2B data to prevent flash */}
          {isOnB2BPortalUrl && b2bLoading ? (
            <div className="flex flex-col items-center gap-6 w-full mb-4">
              <div className="h-20 md:h-28 lg:h-32" /> {/* Placeholder for logo height */}
            </div>
          ) : isOnB2BPortalUrl && b2bClient ? (
            /* B2B Portal Header */
            <div className="flex flex-col items-center gap-6 w-full mb-4">
              {/* B2B Client Logo or Name */}
              <Link 
                to={dashboardUrl} 
                className="cursor-pointer hover:opacity-90 transition-opacity"
                data-testid="b2b-logo-link"
              >
                {b2bClient.logo_url ? (
                  <img 
                    src={b2bClient.logo_url} 
                    alt={b2bClient.name || 'Client'} 
                    className="max-h-20 md:max-h-28 lg:max-h-32 object-contain"
                    style={{ maxWidth: '400px' }}
                  />
                ) : (
                  <h1 
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center"
                    style={{ color: b2bColors?.primary || 'var(--theme-primary)' }}
                  >
                    {/* Show company name (e.g., "Brit & Co") if available, otherwise app_name */}
                    {b2bClient.name || b2bClient.app_name || 'Journal'}
                  </h1>
                )}
              </Link>
            </div>
          ) : !isOnB2BPortalUrl ? (
            /* Main App Header - Original UnCloned Journal branding */
            <div className="flex flex-col lg:flex-row items-center justify-center lg:gap-4 w-full">
              {/* Mobile & Tablet: Custom SVG Logo - Clickable */}
              <Link 
                to={dashboardUrl} 
                className="lg:hidden w-full px-1 cursor-pointer hover:opacity-90 transition-opacity"
                data-testid="mobile-logo-link"
              >
                <div 
                  className={`w-full h-28 sm:h-36 md:h-44 ${isGradientMode ? 'animate-gradient-x' : ''}`}
                  style={{
                    background: isGradientMode 
                      ? `linear-gradient(90deg, var(--theme-logo-primary) 0%, var(--theme-logo-secondary) 25%, var(--theme-logo-primary) 50%, var(--theme-logo-secondary) 75%, var(--theme-logo-primary) 100%)`
                      : 'var(--theme-logo-primary)',
                    backgroundSize: isGradientMode ? '200% 100%' : 'auto',
                    WebkitMaskImage: `url("/new-logo-homepage.png")`,
                    WebkitMaskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskImage: `url("/new-logo-homepage.png")`,
                    maskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                  }}
                />
              </Link>
              {/* Desktop: Custom Logo + sparkles - Clickable */}
              <Link 
                to={dashboardUrl} 
                className="hidden lg:flex items-center justify-center w-full px-16 cursor-pointer hover:opacity-90 transition-opacity"
                data-testid="desktop-logo-link"
              >
                <Sparkles className="w-10 h-10 xl:w-12 xl:h-12 float-animation flex-shrink-0 mr-1" style={{ color: 'var(--theme-logo-primary)' }} />
                <div 
                  className={`h-32 xl:h-36 flex-1 ${isGradientMode ? 'animate-gradient-x' : ''}`}
                  style={{
                    background: isGradientMode 
                      ? `linear-gradient(90deg, var(--theme-logo-primary) 0%, var(--theme-logo-secondary) 25%, var(--theme-logo-primary) 50%, var(--theme-logo-secondary) 75%, var(--theme-logo-primary) 100%)`
                      : 'var(--theme-logo-primary)',
                    backgroundSize: isGradientMode ? '200% 100%' : 'auto',
                    WebkitMaskImage: `url("/new-logo-dashboard.webp")`,
                    WebkitMaskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskImage: `url("/new-logo-dashboard.webp")`,
                    maskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                  }}
                />
                <Sparkles className="w-10 h-10 xl:w-12 xl:h-12 float-animation flex-shrink-0 -ml-8" style={{ color: 'var(--theme-logo-primary)' }} />
              </Link>
            </div>
          ) : null}
          
          {/* Action row - centered below logo like Dashboard */}
          <div className={`flex items-center gap-4 lg:gap-6 mb-4 lg:mb-6 ${isOnB2BPortalUrl && b2bClient ? 'mt-0' : '-mt-2 lg:-mt-4'}`}>
            <Button
              onClick={() => navigate(dashboardUrl)}
              className="btn-primary flex items-center gap-2 lg:gap-3 text-base lg:text-xl px-8 lg:px-14 py-4 lg:py-6"
              style={{ 
                background: isOnB2BPortalUrl && b2bColors 
                  ? `linear-gradient(135deg, ${b2bColors.primary}, ${b2bColors.secondary || b2bColors.primary})`
                  : 'var(--theme-button-bg)',
                color: 'white'
              }}
              data-testid="go-home-btn"
            >
              <Home className="w-5 h-5 lg:w-7 lg:h-7" />
              Go Back Home
            </Button>
            
            <Button
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
              variant="outline"
              className={`flex items-center gap-2 px-6 py-4 lg:py-6 ${unreadCount === 0 ? 'opacity-50' : ''}`}
              style={{ 
                borderColor: isOnB2BPortalUrl && b2bColors ? b2bColors.border : 'var(--theme-border)',
                color: isOnB2BPortalUrl && b2bColors ? b2bColors.textSecondary : 'var(--theme-text-secondary)'
              }}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-5 h-5" />
              <span className="hidden sm:inline">Mark all read</span>
            </Button>
          </div>
        </div>
      </header>
      
      {/* Page Title Section */}
      <div className="max-w-4xl mx-auto w-full mb-6">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center"
            style={{ 
              background: isOnB2BPortalUrl && b2bColors 
                ? `linear-gradient(135deg, ${b2bColors.primary}, ${b2bColors.secondary || b2bColors.primary})`
                : 'var(--theme-button-bg)' 
            }}
          >
            <Bell className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
          </div>
          <div>
            <h1 
              className="text-xl lg:text-2xl font-bold" 
              style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.text : 'var(--theme-text)' }}
            >
              Notifications
            </h1>
            <p 
              className="text-sm" 
              style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.textSecondary : 'var(--theme-text-secondary)' }}
            >
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div 
              className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
              style={{ 
                borderColor: isOnB2BPortalUrl && b2bColors ? b2bColors.accent : 'var(--theme-accent)', 
                borderTopColor: 'transparent' 
              }}
            />
          </div>
        ) : notifications.length === 0 ? (
          <div 
            className="rounded-xl p-12 text-center"
            style={{ 
              background: isOnB2BPortalUrl && b2bColors ? b2bColors.backgroundSecondary : 'var(--theme-background-secondary)',
              border: `1px solid ${isOnB2BPortalUrl && b2bColors ? b2bColors.border : 'var(--theme-border)'}`
            }}
          >
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: isOnB2BPortalUrl && b2bColors ? b2bColors.background : 'var(--theme-background)' }}
            >
              <Bell className="w-10 h-10" style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.textSecondary : 'var(--theme-text-secondary)' }} />
            </div>
            <h2 
              className="text-xl font-semibold mb-2" 
              style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.text : 'var(--theme-text)' }}
            >
              No notifications yet
            </h2>
            <p style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.textSecondary : 'var(--theme-text-secondary)' }}>
              When you receive notifications, they'll appear here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notif) => (
              <div
                key={notif.notification_id}
                onClick={() => {
                  setSelectedNotification(notif);
                  if (!notif.is_read) handleMarkAsRead(notif.notification_id);
                }}
                className="rounded-xl p-5 cursor-pointer transition-all hover:scale-[1.01]"
                style={{ 
                  background: isOnB2BPortalUrl && b2bColors ? b2bColors.backgroundSecondary : 'var(--theme-background-secondary)',
                  border: !notif.is_read 
                    ? `2px solid ${isOnB2BPortalUrl && b2bColors ? b2bColors.accent : 'var(--theme-accent)'}`
                    : `1px solid ${isOnB2BPortalUrl && b2bColors ? b2bColors.border : 'var(--theme-border)'}`
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {notif.image_url ? (
                      <div 
                        className="w-14 h-14 rounded-lg overflow-hidden" 
                        style={{ background: isOnB2BPortalUrl && b2bColors ? b2bColors.background : 'var(--theme-background)' }}
                      >
                        <img src={notif.image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{
                          background: isOnB2BPortalUrl && b2bColors ? `${b2bColors.accent}20` : undefined,
                          color: isOnB2BPortalUrl && b2bColors ? b2bColors.accent : undefined,
                          border: isOnB2BPortalUrl && b2bColors ? `1px solid ${b2bColors.accent}30` : undefined
                        }}
                        className={isOnB2BPortalUrl && b2bColors ? 'w-12 h-12 rounded-full flex items-center justify-center' : `w-12 h-12 rounded-full flex items-center justify-center ${getTypeColor(notif.type)}`}
                      >
                        {getIcon(notif.type)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 
                        className="font-semibold" 
                        style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.text : 'var(--theme-text)' }}
                      >
                        {notif.title}
                      </h3>
                      {!notif.is_read && (
                        <span 
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: isOnB2BPortalUrl && b2bColors ? b2bColors.accent : 'var(--theme-accent)' }}
                        />
                      )}
                    </div>
                    <p 
                      className="text-sm line-clamp-2" 
                      style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.textSecondary : 'var(--theme-text-secondary)' }}
                    >
                      {notif.message}
                    </p>
                    <div 
                      className="flex items-center gap-3 mt-3 text-xs" 
                      style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.textSecondary : 'var(--theme-text-secondary)' }}
                    >
                      <span 
                        className="px-2 py-0.5 rounded-full border"
                        style={isOnB2BPortalUrl && b2bColors ? {
                          background: `${b2bColors.accent}20`,
                          color: b2bColors.accent,
                          borderColor: `${b2bColors.accent}30`
                        } : undefined}
                        className={isOnB2BPortalUrl && b2bColors ? 'px-2 py-0.5 rounded-full border' : `px-2 py-0.5 rounded-full border ${getTypeColor(notif.type)}`}
                      >
                        {notif.type}
                      </span>
                      <span>{format(new Date(notif.created_at), 'MMM d, h:mm a')}</span>
                      {notif.image_url && (
                        <span className="flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" /> Has image
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notif.notification_id);
                    }}
                    className="p-2 rounded-full hover:bg-red-500/20 text-red-400/60 hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer - Same as Dashboard, with B2B branding */}
      <Footer 
        appName={isOnB2BPortalUrl && b2bClient ? (b2bClient.app_name || b2bClient.name) : null}
        companyName={isOnB2BPortalUrl && b2bClient ? (b2bClient.company_name || b2bClient.name) : null}
        showPoweredBy={isOnB2BPortalUrl && b2bClient ? (b2bClient.features?.show_uncloned_branding !== false) : true}
      />

      {/* View Notification Modal */}
      {selectedNotification && !isClosing && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={closeModal}
          data-testid="notification-modal-overlay"
        >
          <div 
            className="w-full max-w-lg rounded-2xl overflow-hidden relative"
            style={{ 
              background: isOnB2BPortalUrl && b2bColors ? b2bColors.backgroundSecondary : 'var(--theme-background-secondary)',
              border: `1px solid ${isOnB2BPortalUrl && b2bColors ? b2bColors.border : 'var(--theme-border)'}`
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="notification-modal-content"
          >
            {/* Close button - positioned absolutely for better accessibility */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
              style={{ color: 'white' }}
              data-testid="notification-modal-close"
              aria-label="Close notification"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Image */}
            {selectedNotification.image_url && (
              <div 
                className="w-full aspect-square" 
                style={{ background: isOnB2BPortalUrl && b2bColors ? b2bColors.background : 'var(--theme-background)' }}
              >
                <img 
                  src={selectedNotification.image_url} 
                  alt={selectedNotification.title}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                {getIcon(selectedNotification.type)}
                <span 
                  className="px-2 py-0.5 rounded-full text-xs border"
                  style={isOnB2BPortalUrl && b2bColors ? {
                    background: `${b2bColors.accent}20`,
                    color: b2bColors.accent,
                    borderColor: `${b2bColors.accent}30`
                  } : undefined}
                  className={isOnB2BPortalUrl && b2bColors ? 'px-2 py-0.5 rounded-full text-xs border' : `px-2 py-0.5 rounded-full text-xs border ${getTypeColor(selectedNotification.type)}`}
                >
                  {selectedNotification.type}
                </span>
              </div>
              
              <h2 
                className="text-xl font-bold mb-2" 
                style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.text : 'var(--theme-text)' }}
              >
                {selectedNotification.title}
              </h2>
              <p 
                className="whitespace-pre-wrap mb-4" 
                style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.textSecondary : 'var(--theme-text-secondary)' }}
              >
                {selectedNotification.message}
              </p>
              
              <div 
                className="flex items-center justify-between pt-4"
                style={{ borderTop: `1px solid ${isOnB2BPortalUrl && b2bColors ? b2bColors.border : 'var(--theme-border)'}` }}
              >
                <span 
                  className="text-sm" 
                  style={{ color: isOnB2BPortalUrl && b2bColors ? b2bColors.textSecondary : 'var(--theme-text-secondary)' }}
                >
                  {format(new Date(selectedNotification.created_at), 'MMMM d, yyyy • h:mm a')}
                </span>
                <Button
                  onClick={() => handleDelete(selectedNotification.notification_id)}
                  variant="outline"
                  size="sm"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
